import { useCallback, useMemo, useRef } from 'react'
import type { NavigateFunction } from 'react-router-dom'
import { navigateWithThreadPreservation } from '../utils/thread-navigation'

export type MobileSurface = 'chat' | 'page'
export type PageDepth = 'root' | 'detail' | 'edit' | 'modal'
export type SurfaceNavigationIntent =
  | 'routeNavigation'
  | 'threadSelection'
  | 'threadsIndex'
  | 'newConversation'

type SurfaceTargetConfig =
  | MobileSurface
  | {
      surface: MobileSurface
      pageDepth?: PageDepth
    }

interface SurfaceTarget {
  surface: MobileSurface
  pageDepth?: PageDepth
}

interface SurfaceNavigationOptions {
  isMobile: boolean
  layout: Record<string, unknown> | undefined
  navigate: NavigateFunction
  searchParams: URLSearchParams
  setActiveMobilePane: (pane: MobileSurface) => void
  selectThread: (threadId: string) => void
}

interface SurfaceNavigationActionOptions {
  pageDepth?: PageDepth
}

const DEFAULT_TARGETS: Record<SurfaceNavigationIntent, SurfaceTarget> = {
  routeNavigation: { surface: 'page', pageDepth: 'root' },
  threadSelection: { surface: 'chat' },
  threadsIndex: { surface: 'page', pageDepth: 'root' },
  newConversation: { surface: 'chat' },
}

function normalizeSurfaceTarget(
  target: SurfaceTargetConfig | undefined,
  fallback: SurfaceTarget
): SurfaceTarget {
  if (!target) return fallback
  if (target === 'chat' || target === 'page') {
    return {
      surface: target,
      pageDepth: target === 'page' ? fallback.pageDepth ?? 'root' : undefined,
    }
  }

  return {
    surface: target.surface,
    pageDepth: target.surface === 'page' ? target.pageDepth ?? fallback.pageDepth ?? 'root' : undefined,
  }
}

export function useSurfaceNavigation({
  isMobile,
  layout,
  navigate,
  searchParams,
  setActiveMobilePane,
  selectThread,
}: SurfaceNavigationOptions) {
  const pageDepthRef = useRef<PageDepth>('root')
  const lastIntentRef = useRef<SurfaceNavigationIntent | null>(null)

  const surfaceTargets = useMemo(() => {
    const configuredTargets = (layout?.mobileSurfaceTargets ?? {}) as Partial<
      Record<SurfaceNavigationIntent, SurfaceTargetConfig>
    >

    return {
      routeNavigation: normalizeSurfaceTarget(configuredTargets.routeNavigation, DEFAULT_TARGETS.routeNavigation),
      threadSelection: normalizeSurfaceTarget(configuredTargets.threadSelection, DEFAULT_TARGETS.threadSelection),
      threadsIndex: normalizeSurfaceTarget(configuredTargets.threadsIndex, DEFAULT_TARGETS.threadsIndex),
      newConversation: normalizeSurfaceTarget(configuredTargets.newConversation, DEFAULT_TARGETS.newConversation),
    }
  }, [layout])

  const applySurfaceIntent = useCallback(
    (intent: SurfaceNavigationIntent, options?: SurfaceNavigationActionOptions) => {
      lastIntentRef.current = intent

      const configuredTarget = surfaceTargets[intent]
      const resolvedTarget: SurfaceTarget = {
        surface: configuredTarget.surface,
        pageDepth:
          configuredTarget.surface === 'page'
            ? options?.pageDepth ?? configuredTarget.pageDepth ?? 'root'
            : undefined,
      }

      if (resolvedTarget.pageDepth) {
        pageDepthRef.current = resolvedTarget.pageDepth
      }

      if (isMobile) {
        setActiveMobilePane(resolvedTarget.surface)
      }
    },
    [isMobile, setActiveMobilePane, surfaceTargets]
  )

  const openRoute = useCallback(
    (path: string, options?: SurfaceNavigationActionOptions) => {
      applySurfaceIntent('routeNavigation', options)
      navigateWithThreadPreservation(navigate, path, searchParams)
    },
    [applySurfaceIntent, navigate, searchParams]
  )

  const openThread = useCallback(
    (threadId: string) => {
      applySurfaceIntent('threadSelection')
      selectThread(threadId)
    },
    [applySurfaceIntent, selectThread]
  )

  const openThreadsIndex = useCallback(() => {
    applySurfaceIntent('threadsIndex', { pageDepth: 'root' })
    navigateWithThreadPreservation(navigate, '/threads', searchParams)
  }, [applySurfaceIntent, navigate, searchParams])

  return {
    openRoute,
    openThread,
    openThreadsIndex,
    applySurfaceIntent,
    pageDepthRef,
    lastIntentRef,
  }
}
